import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Nav } from "../components/Nav";
import { useAuth } from "../AuthContext";
import { useLang } from "../i18n";
import { AREA_META } from "../constants/areaMeta";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function Onboarding() {
  const { setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [goal, setGoal] = useState("");
  const [focus, setFocus] = useState([]);
  const [sports, setSports] = useState("");
  const [frequency, setFrequency] = useState("freq_1_2");
  const [diet, setDiet] = useState("completo");
  const [age, setAge] = useState("");
  const [conditions, setConditions] = useState("");
  const [pain, setPain] = useState("");
  const [busy, setBusy] = useState(false);
  const areas = Object.keys(AREA_META);

  const toggle = (a) => setFocus((f) => (f.includes(a) ? f.filter((x) => x !== a) : [...f, a]));

  const submit = async () => {
    if (!goal.trim()) return toast.error("Inserisci un obiettivo");
    setBusy(true);
    try {
      const res = await api.post("/onboarding", {
        goal, focus_areas: focus, sports, frequency, diet_preference: diet,
        age: age ? parseInt(age, 10) : null, chronic_conditions: conditions, pain_areas: pain,
      });
      setUser(res.data);
      navigate("/dashboard");
    } catch (e) {
      toast.error("Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App min-h-screen">
      <Nav />
      <div className="max-w-2xl mx-auto px-6 py-14">
        <h1 className="font-serif text-4xl tracking-tight text-[#2B4A59] mb-2">{t("onboard_title")}</h1>
        <p className="text-[#6E6B65] mb-8">{t("onboard_sub")}</p>

        <label className="block font-semibold mb-2">{t("goal_label")}</label>
        <Textarea
          data-testid="onboard-goal"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          placeholder="Es. Dormire meglio e avere più energia durante la giornata"
          className="rounded-xl mb-8 min-h-[100px]"
        />

        <label className="block font-semibold mb-3">{t("focus_label")}</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
          {areas.map((a) => {
            const Icon = AREA_META[a].icon;
            const active = focus.includes(a);
            return (
              <button
                key={a}
                data-testid={`focus-${a}`}
                onClick={() => toggle(a)}
                className={`rounded-xl p-4 border text-left transition-all ${active ? "border-[#3F6E85] bg-[#EAF0EC]" : "border-stone-200 bg-white hover:border-stone-300"}`}
              >
                <Icon className="w-5 h-5 mb-2" style={{ color: AREA_META[a].color }} />
                <span className="text-sm font-medium">{t(a)}</span>
              </button>
            );
          })}
        </div>

        <label className="block font-semibold mb-2">{t("onboard_sports")}</label>
        <Input
          data-testid="onboard-sports"
          value={sports}
          onChange={(e) => setSports(e.target.value)}
          placeholder={t("sports_placeholder")}
          className="rounded-xl mb-8"
        />

        <div className="grid sm:grid-cols-2 gap-6 mb-10">
          <div>
            <label className="block font-semibold mb-2">{t("onboard_frequency")}</label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger data-testid="onboard-frequency" className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="freq_none">{t("freq_none")}</SelectItem>
                <SelectItem value="freq_1_2">{t("freq_1_2")}</SelectItem>
                <SelectItem value="freq_3_4">{t("freq_3_4")}</SelectItem>
                <SelectItem value="freq_5plus">{t("freq_5plus")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block font-semibold mb-2">{t("onboard_diet")}</label>
            <Select value={diet} onValueChange={setDiet}>
              <SelectTrigger data-testid="onboard-diet" className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="completo">{t("diet_completo")}</SelectItem>
                <SelectItem value="vegetariano">{t("diet_vegetariano")}</SelectItem>
                <SelectItem value="vegano">{t("diet_vegano")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 mb-4">
          <div>
            <label className="block font-semibold mb-2">{t("onboard_age")}</label>
            <Input data-testid="onboard-age" type="number" min="14" max="110" value={age} onChange={(e) => setAge(e.target.value)} className="rounded-xl" />
          </div>
          <div className="sm:col-span-2">
            <label className="block font-semibold mb-2">{t("onboard_conditions")}</label>
            <Input data-testid="onboard-conditions" value={conditions} onChange={(e) => setConditions(e.target.value)} placeholder={t("conditions_placeholder")} className="rounded-xl" />
          </div>
        </div>
        <div className="mb-4">
          <label className="block font-semibold mb-2">{t("onboard_pain")}</label>
          <Input data-testid="onboard-pain" value={pain} onChange={(e) => setPain(e.target.value)} placeholder={t("pain_placeholder")} className="rounded-xl" />
        </div>
        <p className="text-xs text-[#6E6B65] mb-10">{t("onboard_health_note")}</p>

        <Button data-testid="onboard-submit" onClick={submit} disabled={busy} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] h-11 px-8">
          {busy ? "..." : t("save")}
        </Button>
      </div>
    </div>
  );
}
